import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, Apple } from "lucide-react";
import { useAuth } from "../lib/authContext";
import { formatApiError } from "../lib/authApi";
import { Input } from "../components/ui/input";
import PasswordInput from "../components/PasswordInput";
import GoogleSignInButton from "../components/GoogleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const me = await login({ email: email.trim().toLowerCase(), password });
      toast.success("Bem-vindo!");
      // Prioridade: ?next= explícito > onboarding (se faltar) > home.
      // FASE 2 — admin NÃO é enviado ao painel após login; acessa /admin
      // explicitamente ou pelo item "Painel Admin" do menu.
      const isAdmin = me?.role === "admin";
      const needsOnboarding = !isAdmin && !me?.spiritual_profile;
      let target = next || "/";
      if (needsOnboarding && !next) {
        target = "/bem-vindo?next=%2F";
      }
      navigate(target, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto section-padding py-10" data-testid="login-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
        data-testid="login-back"
      >
        <ArrowLeft className="w-4 h-4" /> Início
      </Link>

      <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Sua conta</p>
      <h1 className="font-display text-4xl text-navy mt-2">Entrar</h1>
      <p className="text-navy/65 mt-3 text-sm">
        Continue sua caminhada de qualquer dispositivo.
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
        <GoogleSignInButton next={next} testid="login-google" />
        <button
          type="button"
          disabled
          aria-disabled="true"
          data-testid="login-apple-soon"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-navy/15 bg-white text-navy/40 px-6 py-3 text-[11px] uppercase tracking-widest cursor-not-allowed"
          title="Em breve"
        >
          <Apple className="w-4 h-4" />
          Entrar com Apple
          <span className="ml-1 px-2 py-0.5 rounded-full text-[9px] bg-navy/5 border border-navy/10 tracking-[0.2em]">
            em breve
          </span>
        </button>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-navy/40">
          <span className="flex-1 h-px bg-border" />
          ou com e-mail
          <span className="flex-1 h-px bg-border" />
        </div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.3em] text-navy/55">E-mail</span>
          <div className="relative mt-2">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
            <Input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              className="pl-10 h-11 bg-white"
              data-testid="login-email"
            />
          </div>
        </label>

        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.3em] text-navy/55">Senha</span>
          <div className="mt-2">
            <PasswordInput
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              testId="login-password"
              toggleTestId="login-password-toggle"
            />
          </div>
        </label>

        {error && (
          <p className="text-sm text-red-600" data-testid="login-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="login-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy disabled:opacity-50 px-6 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
        >
          {submitting ? "Entrando..." : "Entrar"}
        </button>

        <div className="flex items-center justify-between text-[12px]">
          <Link
            to="/esqueci-senha"
            className="text-navy/60 hover:text-gold"
            data-testid="login-forgot-link"
          >
            Esqueci minha senha
          </Link>
          <Link
            to={`/cadastro${next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}`}
            className="text-navy/60 hover:text-gold"
            data-testid="login-register-link"
          >
            Criar conta
          </Link>
        </div>
      </form>
    </div>
  );
}
